import { useEffect, useMemo } from 'react'

import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'

import type {
  Condition,
  ConditionalOperator,
  ConditionPropertyEditorComponentProps
} from 'Automations/nodeDefinitions/conditions'
import {
  isQuotePricingComparison,
  isQuoteComparisonSubOperator,
  readPreviousApprovalValue,
  type QuotePricingComparison,
  type QuoteComparisonSubOperator
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  useQuoteReviewNodes,
  type ReviewNodeOption
} from './useQuoteReviewNodes'
import {
  expressionFor,
  expressionOptions,
  getRemainingExpressions,
  ownExpressionOf,
  hasPricingComparisons,
  isExhausted,
  isQuoteComparisonField,
  parseExpression,
  DEFAULT_SUB_OPERATOR
} from '../quoteComparison'

/**
 * What a PREVIOUS_APPROVAL row stores, with every field normalised so
 * `undefined` is the only way to say "unset". A stored row can carry a review
 * step the graph no longer reaches, or a comparison from a field that no longer
 * offers one, and neither should read as a choice the user made.
 */
export type PreviousApprovalRow = {
  reviewNodeId: string | undefined
  subOperator: QuoteComparisonSubOperator | undefined
  comparison: QuotePricingComparison | undefined
}

export const readRow = (value: unknown): PreviousApprovalRow => {
  const stored = readPreviousApprovalValue(value)

  return {
    reviewNodeId: stored.reviewNodeId || undefined,
    subOperator: isQuoteComparisonSubOperator(stored.operator)
      ? stored.operator
      : undefined,
    comparison: isQuotePricingComparison(stored.comparison)
      ? stored.comparison
      : undefined
  }
}

/**
 * The expressions sibling rows already contribute on one review step. A sibling
 * with no step or no field yet contributes nothing, and a row comparing
 * something other than a previous approval has no step at all.
 */
export const usedExpressionsOnReviewNode = ({
  siblingConditions,
  reviewNodeId
}: {
  siblingConditions: Array<Condition>
  reviewNodeId: string | undefined
}): string[] =>
  reviewNodeId
    ? siblingConditions.reduce<string[]>((used, sibling) => {
        if (
          !sibling.operator ||
          readRow(sibling.value).reviewNodeId !== reviewNodeId
        ) {
          return used
        }
        return [
          ...used,
          expressionFor({ operator: sibling.operator, value: sibling.value })
        ]
      }, [])
    : []

/**
 * Everything a PREVIOUS_APPROVAL row's editor needs: which fields and
 * comparisons are still free, and what the row effectively holds once defaults
 * and sibling-driven fallbacks are applied.
 *
 * Cross-row rules are scoped per review step and per expression. Several rows
 * may compare the same step — its prices and its discounts are different
 * questions — but no two may take the same expression, and a step is exhausted
 * only once every expression is.
 *
 * The effective values are committed back as soon as the row has a step and a
 * field, so what is stored matches what is shown.
 */
export const usePreviousApprovalOperatorState = (params: {
  /** Every node in the workflow; the review steps among them are offered. */
  nodes: AutomationsPresentationV1Node[]
  operator: string
  row: PreviousApprovalRow
  operators: Record<string, ConditionalOperator>
  siblingConditions: Array<Condition>
  onChange: ConditionPropertyEditorComponentProps['onChange']
}): {
  reviewNodeOptions: ReviewNodeOption[]
  /** The step the row reads as: its own if free, else the first that is. */
  reviewNodeId: string | undefined
  /** Every way of comparing still free, pricing's comparisons among them. */
  expressionOptions: Array<{ label: string; value: string }>
  expressionDisabled: boolean
  /** The one the row reads as, as a value for the dropdown. */
  expression: string
  subOperator: QuoteComparisonSubOperator
} => {
  const { nodes, operator, row, operators, siblingConditions, onChange } =
    params

  const exhaustedReviewNodeIds = useMemo(
    () =>
      siblingReviewNodeIds(siblingConditions).filter(
        id =>
          id !== row.reviewNodeId &&
          isExhausted(
            usedExpressionsOnReviewNode({ siblingConditions, reviewNodeId: id })
          )
      ),
    [siblingConditions, row.reviewNodeId]
  )

  const reviewNodeOptions = useQuoteReviewNodes(nodes).filter(
    option =>
      option.value === row.reviewNodeId ||
      !exhaustedReviewNodeIds.includes(option.value)
  )
  // A row opens on the first step still free, so the common case — one review
  // step — needs no pick at all. Where that step sits is validation's to judge.
  // A stored step the workflow no longer holds reads as unset, so the row heals
  // onto a real one rather than pointing at a node that isn't there.
  const storedStepExists = reviewNodeOptions.some(
    option => option.value === row.reviewNodeId
  )
  const reviewNodeId = storedStepExists
    ? row.reviewNodeId
    : reviewNodeOptions.at(0)?.value

  const usedExpressions = usedExpressionsOnReviewNode({
    siblingConditions,
    reviewNodeId
  })
  const showComparisons = hasPricingComparisons(operator)
  const remainingExpressions = getRemainingExpressions({
    usedExpressions,
    ownExpression: ownExpressionOf({ operator, comparison: row.comparison })
  })
  // This row's own pick stays listed, so a stored condition still renders and
  // the user can switch back to it.
  const options = expressionOptions({ remainingExpressions, operators })

  // A pricing row with no comparison takes the first still free, so it can't
  // duplicate a sibling. 'ALL' needs a step with none free — unreachable from
  // the editor.
  const comparison =
    row.comparison ?? firstFreePricingComparison(remainingExpressions) ?? 'ALL'
  const subOperator = row.subOperator ?? DEFAULT_SUB_OPERATOR

  const storedComparison = showComparisons ? comparison : undefined
  const fieldSelected = isQuoteComparisonField(operator)

  useEffect(() => {
    if (!reviewNodeId || !fieldSelected) return
    // The step counts here too: a row that opened on the default holds one it
    // has never stored.
    if (
      row.reviewNodeId === reviewNodeId &&
      row.subOperator === subOperator &&
      row.comparison === storedComparison
    ) {
      return
    }

    onChange({
      value: {
        reviewNodeId,
        operator: subOperator,
        comparison: storedComparison
      }
    })
  }, [
    reviewNodeId,
    fieldSelected,
    row.reviewNodeId,
    row.subOperator,
    row.comparison,
    subOperator,
    storedComparison,
    onChange
  ])

  return {
    reviewNodeOptions,
    reviewNodeId,
    expressionOptions: options,
    expressionDisabled: options.length === 0,
    expression: fieldSelected
      ? expressionFor({ operator, value: { comparison: storedComparison } })
      : '',
    subOperator
  }
}

/** The first pricing comparison a subject still has free, if any. */
const firstFreePricingComparison = (
  remainingExpressions: string[]
): QuotePricingComparison | undefined =>
  remainingExpressions.reduce<QuotePricingComparison | undefined>(
    (found, expression) => found ?? parseExpression(expression).comparison,
    undefined
  )

/** Every review step a sibling row targets, once each. */
const siblingReviewNodeIds = (siblingConditions: Array<Condition>): string[] =>
  Array.from(
    new Set(
      siblingConditions.reduce<string[]>((ids, sibling) => {
        const { reviewNodeId } = readRow(sibling.value)
        return reviewNodeId ? [...ids, reviewNodeId] : ids
      }, [])
    )
  )
